import { useTranslation } from "react-i18next";
import { canAccess, roleAccess } from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useInvoices, useProformaInvoices, useQuotations } from "@/api/hooks";
import { Badge } from "@/components/ui/Badge";
import { DocumentColumn as Column, DocumentRow as Row, RecentDocumentsPanel } from "@/components/domain/RecentDocuments";
import { formatMoney } from "@/domain/money";

/**
 * Chứng từ gần đây của một khách, trên trang chi tiết khách (18/09/2026).
 *
 * Anh Toàn đồng ý khi rà màn hình Khách hàng: mở một khách là phải thấy ngay khách đang có PI nào, đã thu
 * tiền tới đâu, báo giá và hoá đơn nào — trước đây phải sang từng màn hình rồi lọc theo khách. Mỗi loại
 * 5 chứng từ mới nhất, "Xem tất cả" mở đúng danh sách đã lọc theo khách này (các trang danh sách đọc
 * `?customerId=`). Loại nào tài khoản không được xem thì không hiện, cũng không gọi API.
 */
export function CustomerDocuments({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const access = user ? (user.access ?? roleAccess(user.role)) : null;
  const may = (menu: string) => !!access && canAccess(access, menu, "view");
  const params = { customerId, page: 1, pageSize: 5 };
  const pis = useProformaInvoices(params, may("proformaInvoices"));
  const quotations = useQuotations(params, may("quotations"));
  const invoices = useInvoices(params, may("invoices"));
  const q = encodeURIComponent(customerId);

  const columns = [
    may("proformaInvoices") && (
      <Column key="pi" title={t("nav.proformaInvoices")} total={pis.data?.total} loading={pis.isLoading} error={pis.error} allTo={`/proforma-invoices?customerId=${q}`}>
        {pis.data?.data.map((p) => {
          const state = p.paid >= p.totalAmount && p.totalAmount > 0 ? "paid" : p.paid > 0 ? "partial" : "unpaid";
          return (
            <Row key={p.id} to={`/proforma-invoices/${p.id}`} no={p.piNo} date={p.date} money={formatMoney(p.totalAmount, p.currency)}>
              <Badge tone={state === "paid" ? "ok" : state === "partial" ? "warn" : "neutral"}>{t(`status.${state}`)}</Badge>
            </Row>
          );
        })}
      </Column>
    ),
    may("quotations") && (
      <Column key="qt" title={t("nav.quotations")} total={quotations.data?.total} loading={quotations.isLoading} error={quotations.error} allTo={`/quotations?customerId=${q}`}>
        {quotations.data?.data.map((r) => (
          // Báo giá là bảng đơn giá theo mặt hàng: hiện số mặt hàng và mã hàng như trang Báo giá, không hiện tổng.
          <Row key={r.id} to={`/quotations/${r.id}`} no={r.quotationNo} date={r.issueDate} money={t("customers.itemCount", { count: r.itemCount })}>
            {r.itemPreview && <span className="max-w-40 truncate font-mono text-2xs text-ink-faint" title={r.itemPreview}>{r.itemPreview}</span>}
          </Row>
        ))}
      </Column>
    ),
    may("invoices") && (
      <Column key="inv" title={t("nav.invoices")} total={invoices.data?.total} loading={invoices.isLoading} error={invoices.error} allTo={`/invoices?customerId=${q}`}>
        {invoices.data?.data.map((r) => (
          <Row key={r.id} to={`/invoices/${r.id}`} no={r.invNo} date={r.date} money={formatMoney(r.totalAmount, r.currency)}>
            {r.piNo && <span className="font-mono text-2xs text-ink-faint">{t("customers.piRef", { no: r.piNo })}</span>}
          </Row>
        ))}
      </Column>
    ),
  ].filter(Boolean);
  return <RecentDocumentsPanel title={t("customers.documents")} hint={t("customers.documentsHint")} columns={columns} />;
}
